<template>
	<Trap :disabled="!showTrap">
		<div
			v-if="active && hasContentSlot"
			:class="[
				`modal modal--${state}`,
				{
					'modal--oversized': isOversized,
				},
			]"
			role="dialog"
			aria-modal="true"
		>
			<div
				class="modal__background"
				@click.stop="outsideClick"
			/>

			<div
				class="modal__scroller"
				@click.stop="outsideClick"
			>
				<div
					class="modal__wrapper"
					tabindex="0"
					@click.stop
				>
					<div
						v-if="state !== 'normal'"
						class="modal__top"
					/>

					<div
						ref="content"
						class="modal__container"
					>
						<div class="modal__header">
							<div
								v-if="state === 'normal'"
								class="modal__title"
							>
								{{ title }}
							</div>

							<div
								@keyup.enter="close"
								@keyup.space="close"
							>
								<helio-icon
									v-if="closeable"
									class="modal__close"
									token="times"
									weight="light"
									tabindex="0"
									:aria-label="$t('aria-label.close-button')"
									@click="close"
									@keyup.esc="close"
								/>
							</div>
						</div>

						<div class="modal__content">
							<div
								v-if="state !== 'normal'"
								class="modal__icon"
							>
								<helio-icon
									:token="currentIconToken"
									:weight="currentIconWeight"
								/>
							</div>

							<div
								v-if="state !== 'normal'"
								class="modal__title"
							>
								{{ title }}
							</div>

							<slot name="content" />
						</div>

						<div
							v-if="hasFooterSlot"
							class="modal__footer"
						>
							<slot name="footer" />
						</div>
					</div>
				</div>
			</div>
		</div>
	</Trap>
</template>

<script>
import Trap from 'vue-focus-lock';
import Icon from '../icon/Icon.vue';

export default {
	name: 'Modal',

	components: {
		Trap: Trap,
		'helio-icon': Icon,
	},

	props: {
		active: {
			type: Boolean,
			required: true,
			description: 'Sets whether the Modal will be displayed',
		},

		closeOnOutsideClick: {
			type: Boolean,
			required: false,
			default: false,
			description: 'Sets whether a user can close the Modal by clicking elsewhere on the page',
		},

		closeable: {
			type: Boolean,
			required: false,
			default: true,
			description: 'Sets whether the Modal can be closed by a user',
		},

		iconToken: {
			type: String,
			required: false,
			default: null,
			description: 'Sets whether the Modal will have an icon and which icon will be displayed',
		},

		iconWeight: {
			type: String,
			required: false,
			default: null,
			validator: value => [
				'bold',
				'brand',
				'light',
				'regular',
			].indexOf(value) !== -1,
			description: 'Sets the weight of the icon',
		},

		state: {
			type: String,
			required: false,
			default: 'normal',
			validator: value => [
				'alert',
				'danger',
				'info',
				'normal',
				'success',
			].indexOf(value) !== -1,
			description: 'Sets the state of the Modal',
		},

		title: {
			type: String,
			required: true,
			description: 'Sets the Modal\'s title',
		},
	},

	slots: {
		content: {
			required: true,
			scoped: false,
			description: 'Supplies the main content of the Modal component',
		},

		footer: {
			required: false,
			scoped: false,
			description: 'Supplies the footer content of the Modal component',
		},
	},

	data() {
		return {
			opened: false,
			icons: {
				info: {
					token: 'info-circle',
					weight: 'bold',
				},
				success: {
					token: 'check-circle',
					weight: 'bold',
				},
				alert: {
					token: 'exclamation-triangle',
					weight: 'bold',
				},
				danger: {
					token: 'exclamation-circle',
					weight: 'bold',
				},
			},
			isOversized: false,
			showTrap: false,
			lastFocusedElement: null,
		};
	},

	computed: {
		hasContentSlot() {
			return !!this.$slots.content || !!this.$scopedSlots.content;
		},

		hasFooterSlot() {
			return !!this.$slots.footer || !!this.$scopedSlots.footer;
		},

		currentIconWeight() {
			if (this.iconWeight) {
				return this.iconWeight;
			}

			return this.icons[this.state].weight;
		},

		currentIconToken() {
			if (this.iconToken) {
				return this.iconToken;
			}

			return this.icons[this.state].token;
		},
	},

	watch: {
		active() {
			if (this.opened !== this.active) {
				this.opened = this.active;
			}

			if (this.opened) {
				this.open();
			}

			if (!this.opened) {
				this.close(false);
			}

			this.$nextTick(() => {
				this.onResize();
			});
		},
	},

	created() {
		if (this.active) {
			this.open();
			this.$emit('open');
		}
	},

	mounted() {
		window.addEventListener('keyup', this.checkForEscape);
		window.addEventListener('resize', this.onResize);

		this.$nextTick(() => {
			this.onResize();
		});
	},

	beforeDestroy() {
		window.removeEventListener('keyup', this.checkForEscape);
		window.removeEventListener('resize', this.onResize);
	},

	methods: {
		open() {
			this.lastFocusedElement = document.activeElement;
			this.showTrap = true;

			setTimeout(() => {
				document.body.classList.add('is-modal-opened');
			}, 100);
		},

		close(emit = true) {
			this.showTrap = false;

			setTimeout(() => {
				document.body.classList.remove('is-modal-opened');
				if (this.lastFocusedElement) {
					this.lastFocusedElement.focus();
				}
			}, 100);

			if (emit) {
				this.$emit('close');
			}
		},

		checkForEscape(event) {
			const { keyCode } = event;

			if (keyCode === 27 && this.active) {
				this.close();
			}
		},

		outsideClick() {
			if (this.closeOnOutsideClick) {
				this.close();
			}
		},

		onResize() {
			let contentHeight = 0;
			let containerHeight = 0;

			if (this.$refs.content) {
				contentHeight += this.$refs.content.clientHeight;
			}

			if (window) {
				containerHeight = window.innerHeight;
			}

			this.isOversized = (contentHeight > containerHeight - 60);
		},
	},
};
</script>

<style lang="scss" src="./modal.scss" />
